import React, { FC } from 'react'
import IconLock from '../icon/IconLock'
import IconLockOpen from '../icon/IconLockOpen'
import './AccessBanner.less'
import { Box, Flex, Heading } from 'front-end/src/components'
import { Radio, RadioDescription } from '../form/radio/Radio'
import { RadioGroup } from '../../index'
import { t, Trans } from '@lingui/macro'
import { brandColors } from 'shared/basicPalette'
import { useLooopTheme } from '../../../public-api/hooks/useLooopTheme'
import { radii } from '../../chakraThemeExtension'
import { css } from '@emotion/react'
import { BoxProps } from '@chakra-ui/react'

interface IProps {
  subject: string
  isPublic: boolean
  onToggle: (newState: string) => void
  noteOpen?: string
  notePrivate?: string
  publicDesc?: string
  privateDesc?: string
}

export const AccessBanner: React.FC<IProps> = ({
  subject,
  isPublic,
  noteOpen = '',
  notePrivate = '',
  publicDesc = '',
  privateDesc = '',
  onToggle
}: IProps) => {
  const publicDescription =
    publicDesc ||
    t`Any people within your organisation will be able to view this ${subject}. ${noteOpen}`
  const privateDescription =
    privateDesc ||
    t`Only people with access will be able to view this ${subject}. ${notePrivate}`

  return (
    <RadioGroup
      as={Flex}
      onChange={onToggle}
      value={`${isPublic}`}
      mb="32px"
      css={css`
        .chakra-radio__label {
          flex: 1;
        }
        .chakra-radio {
          display: flex;
        }
      `}
    >
      <AccessBannerItem mr="16px" isActive={isPublic}>
        <Radio value="true" dataTest="PublicOptionRadio">
          <Flex align="center" cursor="pointer" justifyContent="space-between">
            <Box>
              <Heading size="md">
                <Trans>Public</Trans>
              </Heading>
              <RadioDescription>{publicDescription}</RadioDescription>
            </Box>

            <IconLockOpen
              color={brandColors.grey[500]}
              mr="8px"
              ml="4px"
              fontSize="32px"
            />
          </Flex>
        </Radio>
      </AccessBannerItem>
      <AccessBannerItem isActive={!isPublic}>
        <Radio value="false" dataTest="PrivateOptionRadio">
          <Flex align="center" cursor="pointer" justifyContent="space-between">
            <Box>
              <Heading size="md">
                <Trans>Private</Trans>
              </Heading>
              <RadioDescription>{privateDescription}</RadioDescription>
            </Box>

            <IconLock
              color={brandColors.grey[500]}
              mr="8px"
              ml="4px"
              fontSize="32px"
            />
          </Flex>
        </Radio>
      </AccessBannerItem>
    </RadioGroup>
  )
}

const AccessBannerItem: FC<
  BoxProps & {
    isActive?: boolean
  }
> = ({ isActive, children, ...rest }) => {
  const { theme } = useLooopTheme()

  return (
    <Box
      borderRadius={radii.md}
      border="1px solid"
      borderColor={
        isActive ? theme.colors.buttonPrimary[500] : brandColors.grey[300]
      }
      p="12px"
      flex={1}
      cursor="pointer"
      {...rest}
    >
      {children}
    </Box>
  )
}
